<template>
  <Card :title="$t('global.two-factor-authentication')">
    <template v-if="!loading2faInfo">
      <ToggleCard :label="`${$t('global.enable')}:`">
        <template #toggle>
          <Toggle @change="toggleShowForm" v-model="showForm" :transitioning="loading" />
        </template>
      </ToggleCard>

      <hr v-if="showForm" />

      <!-- qr code 2fa activation form -->
      <form @submit.prevent="enable2fa" v-if="showForm && !backupCodes.list.length">
        <div class="my-3">{{ $t("global.to-enable-two-factor") }}</div>

        <!-- 2fa qr image for scanning into auth account -->
        <img class="my-3" :src="QRImage" :alt="$t('global.qr-code')" width="200" height="200" />

        <!-- 2fa secret for manual setup (instead of scanning) -->
        <div class="my-3">
          <h1>{{ $t("global.secret") }}:</h1>
          <div class="text-danger">
            {{ secret }}
            <CopyToClipboard class="ml-2" :text="secret" />
          </div>
        </div>

        <!-- 2fa code (one-time-password) input -->
        <div class="row">
          <div class="col-md-6">
            <FormInput
              type="password"
              name="authentication-code"
              v-model="twoFactorFields.authenticationCode"
              testId="authentication-code-input"
              :placeholder="$t('global.authentication-code')"
              :label="`${$t('global.authentication-code')} *`"
              :errors="validation.fields.authenticationCode.errors"
              :dirty="validation.fields.authenticationCode.dirty"
            />
          </div>
        </div>

        <Btn
          variant="primary"
          class="my-2"
          :disabled="registrationLoading"
          :loading="registrationLoading"
          @click="enable2fa"
        >
          {{ $t("global.enable") }}
        </Btn>
      </form>

      <!-- backup codes section -->
      <div v-if="backupCodes.list.length" class="d-flex flex-column">
        <!-- backup code generation warning (old codes won't work, etc.) -->
        <div class="alert-warning p-3 my-4">
          <h1 class="color-alpha">{{ $t("global.warning") }}!</h1>
          <div class="color-alpha">{{ $t("global.backup-codes-can-be-used") }}</div>
        </div>

        <!-- list of backup codes -->
        <div class="mt-3">
          <h3>Backup Codes:</h3>
          <span v-for="(code, i) in backupCodes.list" :key="i" class="mr-1">
            {{ `${code}${i !== backupCodes.list.length - 1 ? "," : ""}` }}
          </span>
          <CopyToClipboard class="ml-2" :text="backupCodes.list.join(' ')" />
        </div>

        <!-- generate new backup codes -->
        <div class="mt-5 d-flex justify-content-end">
          <Btn
            variant="primary"
            @click="generateNewBackupCodes"
            :disabled="loading"
            :loading="loading"
          >
            {{ $t("global.generate-new-backup-codes") }}
          </Btn>
        </div>
      </div>
    </template>

    <!-- loading icon -->
    <div v-if="loading2faInfo">
      <LoadingState :size="50" />
    </div>
  </Card>
</template>

<script>
import { onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import Card from "@/components/display/Card";
import ConfirmDisable2fa from "@/components/smart/modals/ConfirmDisable2fa";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import FormInput from "@/components/display/form/FormInput";
import { fromServer } from "@/utils/translations";
import LoadingState from "@/components/display/LoadingState";
import store from "@/store";
import Toggle from "@/components/display/Toggle";
import ToggleCard from "@/components/display/card-sub-components/CardToggle";
import { twoFactor as twoFactorValidationRules } from "leatherman-js/utils/validations/forms";
import useValidator from "@/utils/useValidator";

export default {
  name: "TwoFactorAuthPage",
  components: {
    Card,
    Toggle,
    ToggleCard,
    FormInput,
    Btn,
    LoadingState,
    CopyToClipboard,
  },
  setup(props, { root }) {
    const loading = ref(false);
    const loading2faInfo = ref(false);
    const registrationLoading = ref(false);
    const twoFactorEnabled = ref(null);
    const showForm = ref(false);

    // should be a list of about 10 backup codes in case the user loses their auth client
    const backupCodes = ref({ list: [] });
    // qr image for scanning into auth account (like google auth)
    const QRImage = ref(null);
    // secret for manually entry (like if you're on desktop)
    const secret = ref(null);

    // login variables and validation
    const twoFactorFields = reactive({
      authenticationCode: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      twoFactorFields,
      twoFactorValidationRules,
      {},
      {
        authenticationCode: {
          required: root.$t("validation.authentication-code-required"),
          regex: root.$t("validation.invalid-authentication-code"),
        },
      }
    );

    // enable 2fa for this user
    const enable2fa = () => {
      registrationLoading.value = true;
      validation.reset();
      validation.validate().then(() => {
        store
          .dispatch("auth/enable2fa", {
            code: twoFactorFields.authenticationCode,
            secret: secret.value,
          })
          .then((response) => {
            twoFactorEnabled.value = response.enabled;

            // new backup codes should have been provided
            backupCodes.value.list = response.backupCodes;
            store.dispatch("toasts/addToast", {
              variant: "success",
              message: root.$t("toasts.two-factor-enabled"),
            });
          })
          .catch((e) => {
            validation.fields.authenticationCode.errors = fromServer(e.request.response);
            validation.fields.authenticationCode.dirty = true;
            store.dispatch("toasts/addToast", {
              variant: "error",
              message: root.$t("toasts.two-factor-failed"),
            });
          })
          .finally(() => {
            registrationLoading.value = false;
          });
      });
    };

    const disabled2fa = () => {
      store
        .dispatch("auth/disable2fa")
        .then(() => {
          showForm.value = false;
          twoFactorFields.authenticationCode = null;
          twoFactorEnabled.value = null;

          // clear backup codes to force form to update
          backupCodes.value.list = [];

          // refresh 2fa info after disabling
          // (get new qr image and secret)
          store.dispatch("toasts/addToast", {
            variant: "success",
            message: root.$t("toasts.two-factor-disabled"),
          });
          get2faInfo();
        })
        .finally(() => {
          loading.value = false;
        });
    };

    // shows / hides the contents of the 2fa form
    const toggleShowForm = (show) => {
      validation.reset();

      // show the form, whether 2fa is currently enabled or not
      if (show) {
        showForm.value = true;
        return;
      }

      // if two factor is enabled, and they toggle it "off", show confirmation modal before disabling 2fa
      // old 2fa one-time-passwords and backup codes won't work anymore if the user reactivates 2fa at a later time
      if (twoFactorEnabled.value) {
        loading.value = true;

        store.dispatch("modals/open", {
          template: ConfirmDisable2fa,
          confirm: disabled2fa, // if they confirm, go ahead and disable 2fa
          close: () => {
            // if they cancel, reset the form
            loading.value = false;
            showForm.value = true;
          },
        });
      } else {
        // if two factor is not enabled, just hide the form
        showForm.value = false;
      }
    };

    // generate new backup codes
    // old backup codes will no longer work
    const generateNewBackupCodes = () => {
      loading.value = true;
      store
        .dispatch("auth/generateBackupCodes")
        .then((list) => {
          backupCodes.value.list = list;
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const get2faInfo = () => {
      loading2faInfo.value = true;
      store
        .dispatch("auth/get2faInfo")
        .then((response) => {
          showForm.value = response.enabled;
          twoFactorEnabled.value = response.enabled;

          // if two factor is enabled, assume backup codes are available
          if (twoFactorEnabled.value) {
            backupCodes.value.list = response.backupCodes;
          } else {
            // else assume a new qr image and secret have been provided
            // the qr image and secret are unique PER PAGE LOAD
            QRImage.value = response.QRImage;
            secret.value = response.secret;
          }
        })
        .finally(() => {
          loading2faInfo.value = false;
        });
    };

    onMounted(() => {
      get2faInfo();
    });

    return {
      twoFactorEnabled,
      showForm,
      registrationLoading,
      loading,
      loading2faInfo,
      QRImage,
      secret,
      twoFactorFields,
      generateNewBackupCodes,
      validation,
      enable2fa,
      toggleShowForm,
      backupCodes,
    };
  },
};
</script>
<style lang="scss" scoped></style>
